import { parseSkillMentionChunks, type SkillMentionChunk } from "@quake/isomorphic/skill-mentions";

export type MessageChunk =
  { type: "text"; value: string } | { type: "mention"; value: { source: string; id: string; label: string } };

export const QUAKE_RECORD_HOST = "quake-record";
export const DEFAULT_RECORD_LINK_LABEL = "Open record";

export function serializeRecordMentionToken(item: { source: string; id: string; label: string }) {
  const clean = (value: string) => value.replaceAll("]", "").replaceAll("|", "").trim();
  const source = clean(String(item.source || ""));
  const id = clean(String(item.id || ""));
  const label = clean(String(item.label || "").replaceAll(/\s+/g, " "));
  return `@record[${source}:${id}|${label}]`;
}

export function parseRecordMentionChunks(content: string): MessageChunk[] {
  const tokenRegex = /@record\[([^:\]|]+):([^\]|]+)\|([^\]]*)\]/g;
  const chunks: MessageChunk[] = [];
  let lastIndex = 0;

  for (const match of content.matchAll(tokenRegex)) {
    const fullToken = match[0] || "";
    const start = match.index ?? 0;
    const end = start + fullToken.length;

    if (start > lastIndex) {
      chunks.push({
        type: "text",
        value: content.slice(lastIndex, start),
      });
    }

    chunks.push({
      type: "mention",
      value: {
        source: (match[1] || "").trim(),
        id: (match[2] || "").trim(),
        label: (match[3] || "").trim() || (match[2] || "").trim(),
      },
    });

    lastIndex = end;
  }

  if (lastIndex < content.length) {
    chunks.push({
      type: "text",
      value: content.slice(lastIndex),
    });
  }

  return chunks.length ? chunks : [{ type: "text", value: content }];
}

export function parseChatMentionChunks(content: string): (MessageChunk | SkillMentionChunk)[] {
  return parseSkillMentionChunks(content).flatMap((chunk): (MessageChunk | SkillMentionChunk)[] =>
    chunk.type === "text" ? parseRecordMentionChunks(chunk.value) : [chunk],
  );
}

export function parseQuakeRecordHref(href?: string) {
  if (!href) return null;

  try {
    const url = new URL(href);
    if (url.host !== QUAKE_RECORD_HOST) return null;

    const [source, ...pathParts] = url.pathname.split("/").filter(Boolean);

    if (source === "custom") {
      const [customModelId, id] = pathParts;
      if (!customModelId || !id) return null;

      return {
        source: "custom",
        id,
        customModelId,
      };
    }

    const [id] = pathParts;
    if (!source || !id) return null;

    return {
      source,
      id,
    };
  } catch {
    return null;
  }
}

import { Children, isValidElement, type ReactNode } from "react";

export function getLinkLabel(children: ReactNode): string {
  return Children.toArray(children)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") return String(child);
      if (!isValidElement(child)) return "";
      return getLinkLabel(child.props.children);
    })
    .join("");
}
